/* eslint-disable */
import { useContext, useEffect, useState } from "react";

import { skipToken } from "@reduxjs/toolkit/query";
import { isAfter } from "date-fns";
import saveAs from "file-saver";

import { Formatters } from "../../common/helpers/formatters";
import {
    CheckCircle,
    ChevronCompactDown,
    ChevronCompactUp,
    DownloadIcon,
    InfoCircle,
    Warning,
} from "../../common/icons";
import { PolicyInstallment } from "../../models/policyInstallment";
import { LocalizationContext } from "../../App";
import { useDownloadInvoiceDocumentQuery, useGetPoliciesQuery } from "../../store/policiesSlice";
import { DownloadInvoiceDocumentRequest } from "../../models/request/DownloadInvoiceDocumentRequest";
import { ProductContext } from "../../context/ProductContext";
import { useAppDispatch } from "../../store/store";
import { logout } from "../../store/authSlice";

type InstallmentCardProps = {
    installment: PolicyInstallment;
};

export const InstallmentCard = ({ installment }: InstallmentCardProps) => {
    const dispatch = useAppDispatch();
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const isOverdue = isAfter(new Date(), installment.dueBy);
    const l10n = useContext(LocalizationContext);
    const { di, ppi } = useContext(ProductContext);
    const { productIdToTypeKeyDict } = useContext(ProductContext);
    const [downloadRequest, setDownloadRequest] = useState<
        DownloadInvoiceDocumentRequest | undefined
    >(undefined);
    const { data: invoiceDocument } = useDownloadInvoiceDocumentQuery(downloadRequest ?? skipToken, {
        refetchOnMountOrArgChange: true,
    });
    const { data: policies, isLoading } = useGetPoliciesQuery();

    useEffect(() => {
        if (invoiceDocument) {
            try {
                saveAs(
                    invoiceDocument as Blob,
                    `${installment.invoiceNumber || "downloaded_document"}.pdf`
                );
                setDownloadRequest(undefined);
            } catch (error) {
                // TODO: Add error handling UI
                console.error("Error downloading the document:", error);
            }
        }
    }, [invoiceDocument]);

    const download = (policyUId: string, invoiceUId: string) => {
        setDownloadRequest(Object.assign({ invoiceUId, policyUId }));
    };

    const getProductTypeKey = (policyId: number) => {
        let productId: number = 0;
        if (policies) {
            productId = policies?.find((policy) => policyId === policy.id)?.productTypeId ?? 0;

            if ([di, ppi].includes(productId)) {
                return productIdToTypeKeyDict[productId];
            }
        }

        dispatch(logout());
        throw new Error(`Unsupported product ID: ${productId}`);
    };

    const GetInstallmentInfo = (policyId: number, installmentNumber: number) => {
        const policy = policies?.find(p => p.id === policyId);
        return policy ? `${installmentNumber}/${policy.numberOfInstallments}` : policyId;
    };

    if (isLoading) return <p className="text-center">{l10n.common.loading}</p>;

    return (
        <div className="card">
            <div className="card-header">
                <span>{l10n.products[getProductTypeKey(installment.policyId)].name}</span>
                {installment.statusId == 4 ? (
                    <CheckCircle className={"h-6 w-6 stroke-declaration-read-icon"} />
                ) : isOverdue ? (
                    <Warning className={"h-6 w-6 invalid-feedback"} />
                ) : (
                    <InfoCircle className={"h-6 w-6"} />
                )}
            </div>

            <div className="grid p-4">
                <div className="flex justify-between">
                    <span>
                        {l10n.policy.installment}&nbsp;
                        {GetInstallmentInfo(installment.policyId, installment.installmentNumber)}
                    </span>
                    <span className="font-semibold">
                        {Formatters.formatCurrencyNumber(installment.amount)}
                    </span>
                </div>
                <div className="flex justify-between">
                    <span>{l10n.label.dueBy}</span>
                    <span className="font-semibold">{Formatters.formatShortDate(installment.dueBy)}</span>
                </div>
            </div>

            <div
                className={`${isExpanded ? "h-36 py-4" : "h-0 py-0"} grid grid-cols-2 gap-4 px-4 text-sm transition-all duration-300 overflow-hidden w-full`}
            >
                <div className="flex flex-col">
                    <span className="text-sm">{l10n.label.issuedAt}</span>
                    <span className="text-base font-semibold">
                        {installment.issuedAt ? Formatters.formatShortDate(installment.issuedAt) : "N/A"}
                    </span>
                </div>

                <div className="flex flex-col">
                    <span className="text-sm">{l10n.label.paidAt}</span>
                    <span className="text-base font-semibold">
                        {installment.paidAt ? Formatters.formatShortDate(installment.paidAt) : "N/A"}
                    </span>
                </div>

                <div className="flex flex-col">
                    <span className="text-sm">{l10n.label.invoiceNumber}</span>
                    <span className="text-base font-semibold">
                        {installment.invoiceNumber ? (
                            <button
                                className="w-full px-2 py-1 inline-flex gap-1 justify-between items-center border border-[var(--petrol3)] rounded-btn"
                                onClick={() => download(installment.policyIdentifier, installment.erpReference)}
                            >
                                <span className="text-sm font-medium leading-none overflow-hidden text-ellipsis whitespace-nowrap">
                                    {installment.invoiceNumber}
                                </span>
                                {downloadRequest ? (
                                    <div className="w-6 ellipsis text-card">&nbsp;</div>
                                ) : (
                                    <DownloadIcon className="shrink-0 w-4 h-4 stroke-card" />
                                )}
                            </button>
                        ) : (
                            "N/A"
                        )}
                    </span>
                </div>

                <div className="flex flex-col">
                    <span className="text-sm">{l10n.label.grossPrice}</span>
                    <span className="text-base font-semibold">
                        {Formatters.formatCurrencyNumber(installment.grossPrice)}
                    </span>
                </div>
            </div>

            <div className="flex justify-center items-center">
                <button onClick={() => setIsExpanded(!isExpanded)}>
                    <span>
                        {isExpanded ? (
                            <ChevronCompactUp className={"w-6 h-6"} />
                        ) : (
                            <ChevronCompactDown className={"w-6 h-6"} />
                        )}
                    </span>
                </button>
            </div>
        </div>
    );
};
